<template>
  <div
    class="inset-box inset-box--member"
    data-ui="inset-box"
  >
    <div class="inset-box__member-name">
      {{ user.firstName }} {{ user.lastName }}
    </div>
    <div
      class="inset-box__member-number"
      data-ui="member-number"
    >
      Member #{{ user.memberNumber }}
    </div>
    <template v-if="showDividendBalance">
      <div
        class="inset-box__member-rewards"
        data-ui="member-rewards"
      >
        Your total REI Rewards
        <CdrPopover
          id="popover-example"
          position="top"
          class="inset-box__member-rewards-info-popover"
        >
          <template #trigger>
            <cdr-button
              class="inset-box__member-rewards-button"
              :icon-only="true"
              aria-haspopup="dialog"
              aria-label="Learn more about Total R E I Rewards"
            >
              <template #icon>
                <IconQuestionStroke inherit-color />
              </template>
            </cdr-button>
          </template>
          <div
            class="inset-box__member-rewards-info-popover-content"
          >
            Includes your annual Co-op Member Reward, typically 10% back annually on eligible
            full-price purchases; and your earned REI Co-op® Mastercard® Rewards (if you’re
            an REI Co-op Mastercard holder).
          </div>
        </CdrPopover>
      </div>
      <div class="inset-box__member-rewards-container">
        <CdrText
          class="inset-box__member-rewards-value"
        >
          {{ formattedDividend }}
        </CdrText>
        <CdrLink
          data-ui="reward-details-button"
          href="/account/member-rewards"
          class="inset-box__rewards-details-button"
          @click="handleRewardsClick"
        >
          See rewards details
        </CdrLink>
      </div>
    </template>
    <CdrButton
      v-if="personalizedTopCategory?.destination"
      class="inset-box__personalized-cta"
      tag="a"
      data-ui="hero-personalized-cta"
      :full-width="false"
      :href="personalizedTopCategory?.destination"
    >
      {{ topCategoryCopy }}
    </CdrButton>
  </div>
</template>

<script setup>
import { computed, inject } from 'vue';

import {
  CdrButton,
  CdrText,
  CdrLink,
  IconQuestionStroke,
  CdrPopover,
} from '@rei/cedar';
import { currencyFormatter } from '../../../utils/formatCurrency';
import { metricsLink } from '../../../utils/analytics';

const props = defineProps({
  personalizedTopCategory: { type: Object, required: false, default: null },
});

const userInformation = inject('userInformation');
const user = computed(() => userInformation.user);
const showDividendBalance = computed(() => {
  const dividendBalance = user?.value?.dividendBalance;
  return dividendBalance > 0 || dividendBalance === 0;
});

const formattedDividend = computed(() => currencyFormatter.format(user?.value?.dividendBalance));
const topCategoryCopy = computed(() => `Shop ${props.personalizedTopCategory?.description?.toLowerCase()}`);

const handleRewardsClick = () => metricsLink('reward-details-link');
</script>

<style lang="scss">
.inset-box--member.inset-box--member {
  padding-bottom: 0;
  margin-bottom: $cdr-space-one-x;
  @include cdr-lg-mq-up {
    margin-bottom: 0;
  }
}

.inset-box__member-name {
  @include cdr-text-heading-serif-600;
  text-align: center;
  max-width: calc(100vw - 100px);
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0 auto;
  padding-bottom: $cdr-space-half-x;

  @include cdr-md-mq-up {
    @include cdr-text-heading-serif-800;
  }
  @include cdr-lg-mq-up {
    max-width: 340px;
  }
}

.inset-box__member-rewards {
  @include cdr-text-eyebrow-100;
  position: relative;
  text-align: center;
  display: flex;
  justify-content: center;

  .inset-box__member-rewards-button {
    width: 26px;
    height: 26px;
    padding: 0 6px 0 0;
    margin-top: -5px;
    margin-left: 3px;
  }
}

.inset-box__member-rewards-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.inset-box__rewards-details-button {
  color: $cdr-color-border-info;
  font-size: 14px;
  display: inline-block;
  padding-left: 6px;
}

.inset-box__member-number {
  @include cdr-text-eyebrow-100;
  text-align: center;
  margin-bottom: 3px;
}

.inset-box__member-rewards-value {
  @include cdr-text-heading-sans-600;
  color: $cdr-color-text-brand;
  display: inline-block;
}

.inset-box__personalized-cta {
  width: 100%;
  justify-content: center;
  max-width: 316px;
  margin: $cdr-space-three-quarter-x auto;
}

.inset-box__member-rewards-info-popover-content {
  @include cdr-text-utility-sans-300;
  text-transform: none;
}
</style>
